import React, { useState, useEffect, useImperativeHandle, Dispatch, SetStateAction, forwardRef } from "react";
import { Grid } from "@mui/material";

// @ts-ignore
import App from "app";
// @ts-ignore
import { StriimTypography, StriimCodeBlock } from "@striim/striim-ui";
import CircularProgress from "@mui/material/CircularProgress";

import { styles } from "./validation-list.styles";
import WarningIcon from "../../../../generic/icon/wizard-icon/warning.svg";
import SuccessIcon from "../../../../generic/icon/wizard-icon/successOutlined.svg";
import ErrorIcon from "../../../../generic/icon/wizard-icon/errorFilled.svg";
import Tracker from "../../../../../core/services/tracker/tracker";
import { TRACKER_STRINGS } from "../../../../../core/services/tracker/constants";
import { ValidationListRef } from "../../../flow-designer/vts/select-tables-modal/components/validation-list/validation-list";

export enum ValidationListStatus {
    Todo = "todo", // Loading
    Success = "success",
    Warning = "warn",
    Failed = "failed"
}

export interface ValidationListElement {
    status: ValidationListStatus; // Initial status
    text: string;
    message?: string;
    children?: object;
    hidden?: boolean; // E.g. for minimal timeout
    method: () => Promise<string | true | { result: any }>;
}

export interface SetAPIStatusProperty {
    isAPISuccessful?: boolean;
    isAPICompleted?: boolean;
    canRetry?: boolean;
}

interface ValidationListProps {
    validationList: ValidationListElement[];
    left?: boolean;
    setAPIStatus?: Dispatch<SetStateAction<SetAPIStatusProperty>>;
    async?: boolean;
    onSuccess: () => void;
    controlAppWizardFooter?: boolean;
}

export const ValidationList = forwardRef<ValidationListRef, ValidationListProps>(
    (
        {
            validationList,
            left = true,
            setAPIStatus = null,
            async = true,
            onSuccess,
            controlAppWizardFooter = true
        }: ValidationListProps,
        ref
    ) => {
        const [validationListState, setValidationListState] = useState(validationList);

        useEffect(() => {
            if (controlAppWizardFooter) App.vent.trigger("appwizard:next:disable");
            executeValidationListAPI();
        }, []);

        useImperativeHandle<ValidationListRef, ValidationListRef>(ref, () => ({
            retryAPI
        }));

        const retryAPI = () => {
            setValidationListState(validationList);
            executeValidationListAPI();
        };

        const trackError = (title, message) => {
            Tracker.getInstance().track(TRACKER_STRINGS.schema.error, {
                id: "",
                event: title,
                message: message
            });
        };

        const updateStatus = async (listItem, index) => {
            let hasFailure,
                hasWarning,
                failedStep = false,
                failAllPending = false;
            const hasChildValidationList = [
                "Validating Permissions",
                "Validating CDC Permissions",
                "Checking Privileges"
            ];
            try {
                let response = await listItem.method();
                // To-Do Remove once the BE is fixed
                if (response === "Completed." || response === "Connection Successful!")
                    response = JSON.stringify({ result: true });
                const parsedResponse = typeof response === "string" ? JSON.parse(response) : response;
                const hasArrayResponse = Array.isArray(parsedResponse?.message);
                if (parsedResponse) {
                    if (parsedResponse?.viewDocumentation) {
                        listItem.viewDocumentation = parsedResponse.viewDocumentation;
                    }
                    // Don't set status here, if response is Array
                    if ((parsedResponse?.result || parsedResponse === true) && !hasArrayResponse) {
                        listItem.status = ValidationListStatus.Success;
                    } else if (parsedResponse.result === false) {
                        failedStep = true;
                    } else if (hasChildValidationList.includes(listItem?.text)) {
                        if (hasArrayResponse) {
                            hasFailure = parsedResponse?.message.some(childList => {
                                return childList.status === ValidationListStatus.Failed && childList.isWarn !== true;
                            });
                            hasWarning = parsedResponse?.message.some(childList => {
                                return childList.isWarn === true;
                            });
                            listItem.status = hasFailure
                                ? ValidationListStatus.Failed
                                : hasWarning
                                ? ValidationListStatus.Warning
                                : ValidationListStatus.Success;
                            listItem.children = parsedResponse?.message;
                        } else {
                            failedStep = true;
                        }
                    } else {
                        failedStep = true;
                    }
                } else {
                    failedStep = true;
                }
                if (failedStep) {
                    listItem.status = ValidationListStatus.Failed;
                    failAllPending = true;
                    listItem.message = hasArrayResponse ? null : parsedResponse.message ?? null;
                    trackError(listItem.text, listItem.message);
                }
            } catch (e) {
                listItem.status = ValidationListStatus.Failed;
                failAllPending = true;
                listItem.message = e.message ? e.message : e;
                trackError(listItem.text, listItem.message);
            }

            setValidationListState(prevstate => {
                prevstate[index] = listItem;
                updateButtonState(prevstate);
                return prevstate;
            });

            return !async ? failAllPending : undefined;
        };

        const executeValidationListAPI = async () => {
            const validationList = [...validationListState];
            if (async) {
                validationList.forEach(async (listItem, index) => {
                    updateStatus(listItem, index);
                });
            } else {
                let failAllPending = false;
                let newList = [...validationList];

                for (let index = 0; index < validationList.length; index++) {
                    const listItem = validationList[index];
                    if (failAllPending) {
                        listItem.status = ValidationListStatus.Failed;
                        newList[index] = listItem;
                        setValidationListState(newList);
                        updateButtonState(newList);
                        trackError(listItem.text, listItem.message);
                    } else {
                        failAllPending = await updateStatus(listItem, index);
                    }
                }
            }
        };

        const updateButtonState = validationList => {
            const isAPICompleted = !validationList.some(item => {
                return item.status === ValidationListStatus.Todo;
            });
            const isAPISuccessful = validationList.every(item => {
                return (
                    item.status === "done" ||
                    item.status === ValidationListStatus.Success ||
                    item.status === ValidationListStatus.Warning
                );
            });
            const canRetry = isAPICompleted && !isAPISuccessful;

            setAPIStatus?.({
                isAPICompleted: isAPICompleted,
                isAPISuccessful: isAPISuccessful,
                canRetry: canRetry
            });
            if (isAPISuccessful) {
                // If Warning is present, Don't transition automatically
                const hasWarning = validationList.some(item => item.status === ValidationListStatus.Warning);
                if (hasWarning && controlAppWizardFooter) {
                    App.vent.trigger("appwizard:next:enable");
                } else onSuccess && onSuccess();
            }
        };

        const customCircularProgress = (
            <span style={{ position: "relative", display: "inline-flex", width: 20, height: 20 }}>
                <CircularProgress
                    variant="determinate"
                    size={24}
                    thickness={8}
                    value={100}
                    sx={{ ...styles.circularProgressBase, ...styles.outerCircularProgress }}
                />
                <CircularProgress
                    disableShrink
                    color="secondary"
                    size={24}
                    thickness={8}
                    sx={{ position: "absolute", top: 0, left: 0, willChange: "transform" }}
                />
            </span>
        );

        const statusMapping = currentStatus => {
            const icons = {
                todo: customCircularProgress,
                success: <SuccessIcon style={styles.svgIcon} />,
                failed: <ErrorIcon style={styles.svgIcon} />,
                done: <SuccessIcon style={styles.svgIcon} />,
                fail: <ErrorIcon style={styles.svgIcon} />,
                warn: <WarningIcon style={styles.svgIcon} />
            };

            return icons[currentStatus];
        };
        const renderListItem = listItem => {
            return (
                <Grid
                    container
                    sx={{
                        ...styles.gridContainerBase,
                        ...(left ? styles.leftPositionedIcon : styles.rightPositionedIcon)
                    }}
                >
                    <Grid item sx={styles.gridstatus}>
                        {statusMapping(listItem.isWarn ? ValidationListStatus.Warning : listItem.status)}
                    </Grid>
                    <Grid item sx={styles.gridItemBase}>
                        <StriimTypography
                            variant="body4"
                            sx={styles.typography}
                            data-test-id={`${(listItem.text || listItem.name).toLowerCase().replaceAll(" ", "-")}-${
                                listItem.status
                            }`}
                        >
                            {listItem.text || listItem.name}
                        </StriimTypography>
                        {listItem.message &&
                            (listItem.status === ValidationListStatus.Failed ||
                                listItem.status === "fail" ||
                                listItem.isWarn) && (
                                <StriimTypography variant="body4" sx={styles.typography}>
                                    {listItem.message}{" "}
                                </StriimTypography>
                            )}
                        {listItem.viewDocumentation || null}
                        {listItem.helpCommand &&
                            listItem.helpCommand !== "null" &&
                            (listItem.status === ValidationListStatus.Failed ||
                                listItem.status === "fail" ||
                                listItem.isWarn) && (
                                <StriimCodeBlock
                                    code={listItem.helpCommand}
                                    copyButton={listItem.instructionType === "SQL"}
                                    isText={listItem.instructionType !== "SQL"}
                                />
                            )}
                        {listItem.children?.map(childItem => renderListItem(childItem))}
                    </Grid>
                </Grid>
            );
        };

        return validationListState?.map(listItem => (listItem.hidden ? null : renderListItem(listItem)));
    }
);

export default ValidationList;
